import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import Markdown from 'react-markdown';
import { Button } from '../../../../web/components/Button';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { SparklesIcon } from '../../../../web/components/svgs/icons/SparklesIcon';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { brandPrimary, charmGray } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { useAISummary } from '../../../hooks/useAISummary';
import { AidaDraggable } from '../../AidaDraggable';
import { DraggableIcon } from '../../svgs/icons/audio/DraggableIcon';
import { useTwilio } from '../context';
import { styleSheet } from './styles';

interface ICallTranscriptModalProps {
	setLastPhoneCall?: (phoneCall: Api.IPhoneCall | null) => void;
	isCallComplete?: boolean;
}

export const CallTranscriptModal: React.FC<ICallTranscriptModalProps> = ({
	setLastPhoneCall,
	isCallComplete = false,
}) => {
	const { realtimeTranscriptionUtterances } = useTwilio();
	const realtimeTranscriptionUtterancesRef = React.useRef<HTMLDivElement>(null);

	// AI Summary functionality
	const { aiResponse, isLoading, hasFetchedFeedback, handleGetPostCallFeedback, resetPostCallFeedback } = useAISummary(
		realtimeTranscriptionUtterances
	);
	const [showAIModal, setShowAIModal] = React.useState(false);

	const [dragging, setDragging] = React.useState<{
		active?: boolean;
		x?: number;
		y?: number;
	}>({ active: false, x: 0, y: 0 });

	// Load position from localStorage on component mount
	React.useEffect(() => {
		try {
			const savedPosition = localStorage.getItem('callTranscriptPosition');
			if (savedPosition) {
				const { x, y } = JSON.parse(savedPosition);
				setDragging(prev => ({ ...prev, x, y }));
			}
		} catch (error) {
			console.error('Error loading CallTranscript position from localStorage:', error);
		}
	}, []);

	const onDrag = (event: DraggableEvent, { x, y }: DraggableData) => {
		event.stopPropagation();
		setDragging({ active: true, x, y });
	};

	const onDragStop = (_: DraggableEvent, { x, y }: DraggableData) => {
		const { active } = dragging;
		if (active) {
			setDragging({ active: false, x, y });

			// Save position to localStorage when drag stops
			try {
				localStorage.setItem('callTranscriptPosition', JSON.stringify({ x, y }));
			} catch (error) {
				console.error('Error saving CallTranscript position to localStorage:', error);
			}
		}
	};

	// Auto-scroll transcription utterances
	React.useEffect(() => {
		if (realtimeTranscriptionUtterancesRef.current) {
			realtimeTranscriptionUtterancesRef.current.scrollTop = realtimeTranscriptionUtterancesRef.current.scrollHeight;
		}
	}, [realtimeTranscriptionUtterances]);

	const handleClose = React.useCallback(() => {
		// Close any open AI Summary modal
		setShowAIModal(false);

		// Reset AI feedback state
		resetPostCallFeedback();

		// Clear the last phone call to dismiss the transcript modal
		if (setLastPhoneCall) {
			setLastPhoneCall(null);
		}
	}, [resetPostCallFeedback, setLastPhoneCall]);

	// Reset AI feedback when call is no longer complete
	React.useEffect(() => {
		if (!isCallComplete) {
			resetPostCallFeedback();
			setShowAIModal(false);
		}
	}, [isCallComplete, resetPostCallFeedback]);

	// Add keyboard support for closing with Escape key
	React.useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleClose]);

	// Early return to hide modal - keeping transcription in memory
	return null;

	return (
		<>
			<AidaDraggable
				onDrag={onDrag}
				onStop={onDragStop}
				defaultPosition={{ x: dragging.x, y: dragging.y }}
				position={{ x: dragging.x, y: dragging.y }}
				handle='.call-transcript-drag-handle'
				isDraggable={true}
			>
				<div className={css(styleSheet.floatingPanel)}>
					<div className={css(styleSheet.root)}>
						<div className={css(styleSheet.header)}>
							<div className={css(styleSheet.oneHalf)}>
								<h5>Live Call Transcript</h5>
							</div>
							{setLastPhoneCall && (
								<div className={css(styleSheet.iconGroup)}>
									{isCallComplete && (
										<Button
											kind='secondary'
											size='small'
											onClick={() => {
												if (!hasFetchedFeedback) {
													handleGetPostCallFeedback();
												}
												setShowAIModal(true);
											}}
											disabled={isLoading}
										>
											<span className={css(styleSheet.aiCallButtonText, baseStyleSheet.truncateText)}>
												<SparklesIcon className={css([styleSheet.smallIcon])} />
												{isLoading ? 'Generating...' : 'AI Summary'}
											</span>
										</Button>
									)}
									<div className='call-transcript-drag-handle'>
										<DraggableIcon fill={brandPrimary} />
									</div>
									<button onClick={handleClose} className={css(styleSheet.iconButton)}>
										<XIcon color={charmGray} height={12} width={12} />
									</button>
								</div>
							)}
						</div>

						<div className={css(styleSheet.realtimeTranscriptionUtterances)} ref={realtimeTranscriptionUtterancesRef}>
							{realtimeTranscriptionUtterances.length === 0 && <div>Call starting...</div>}
							{realtimeTranscriptionUtterances.map((utterance: string, index: number) => {
								return <div key={`${utterance}-${index}`}>{utterance}</div>;
							})}
						</div>
					</div>
				</div>
			</AidaDraggable>

			{/* AI Summary Modal - Outside of draggable to avoid conflicts */}
			<Modal
				isOpen={showAIModal}
				onRequestClose={() => setShowAIModal(false)}
				useStyledHeader
				className='ai-summary-modal'
			>
				<div className={css(styleSheet.modalContainer)}>
					<div className={css(styleSheet.modalHeader)}>
						<SparklesIcon className={css(styleSheet.modalIcon)} />
						<h3 className={css(styleSheet.modalTitle)}>Post-Call AI Feedback</h3>
					</div>
					<div className={css(styleSheet.modalContent)}>
						{isLoading ? (
							<div className={css(styleSheet.loadingContainer)}>
								<LoadingSpinner type='small' />
								<div className={css(styleSheet.loadingText)}>Generating AI feedback...</div>
							</div>
						) : aiResponse ? (
							<Markdown>{aiResponse}</Markdown>
						) : (
							<div className={css(styleSheet.loadingContainerWithColor)}>
								<LoadingSpinner type='small' />
								<div className={css(styleSheet.loadingText)}>Creating AI Conversation...</div>
							</div>
						)}
					</div>
				</div>
			</Modal>
		</>
	);
};
